import React, { useEffect, useMemo, useState } from 'react';
import { Money } from '@arema/components';
import { addCommas } from '@arema/components/Util';
import { Button, Icon, SegmentControl, Table } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IncomeReportData, ReportDownloadLink } from '../../AdminClasses';

interface IncomePromoterData{
	promoter_id: number,
	promoter_name: string,
	user_assigned: number,
	user_assigned_name: string,
	events: number[],
}

interface IncomeData{
	ticket_income: number,
	commission: number,
	total_return: number,
	delivery_cost: number,
	payment_cost: number,
}

interface IncomeReportProps{
	data: {
		promoters: IncomePromoterData[],
		income: IncomeReportData[],
	},
	downloads?: ReportDownloadLink[],
}

enum ReportType{
	TOTALS = 1,
	PROMOTER = 2,
	ASSIGNED = 3,
}

var IncomeReport = (props: IncomeReportProps)=>{
	var { t } = useTranslation();
	var [reportType, setReportType] = useState<ReportType>(ReportType.TOTALS);

	var promoter_data = useMemo(()=>{
		var promoters : (IncomePromoterData & IncomeData)[] = [...props.data.promoters.map(a=>({
			...a,
			events: [],
			ticket_income: 0,
			commission: 0,
			total_return: 0,
			delivery_cost: 0,
			payment_cost: 0,
		}))];
		for(var i of promoters){
			var ev = props.data.income.filter(a=>a.promoter_id===i.promoter_id);

			for(var e of ev){
				if(i.events.indexOf(e.event_id)==-1){
					i.events.push(e.event_id);
				}
				
				i.ticket_income += e.ticket_income;
				i.commission += e.commission;
				i.total_return += e.total_return;
				i.delivery_cost += e.delivery_cost;
				i.payment_cost += e.payment_cost;
			}
		}
		return promoters;
	}, [props.data]);

	var assigned_data = useMemo(()=>{
		var users : (IncomeData & {
			user_id: number,
			user_name: string,
			promoters: number,
			events: number[],
		})[] = [];
		for(var i of props.data.income){
			var ux = users.findIndex(a=>a.user_id===i.user_assigned);
			if(ux==-1){
				ux = users.push({
					user_id: i.user_assigned,
					user_name: i.user_assigned_name,
					promoters: 0,
					events: [],
					ticket_income: 0,
					commission: 0,
					total_return: 0,
					delivery_cost: 0,
					payment_cost: 0,
				})-1;
			}

			if(users[ux].events.indexOf(i.event_id)==-1){
				users[ux].events.push(i.event_id);
			}

			users[ux].ticket_income += i.ticket_income;
			users[ux].commission += i.commission;
			users[ux].total_return += i.total_return;
			users[ux].delivery_cost += i.delivery_cost;
			users[ux].payment_cost += i.payment_cost;
		}

		for(var p of props.data.promoters){
			var ux = users.findIndex(a=>a.user_id===p.user_assigned);
			if(ux===-1) continue;
			users[ux].promoters++;
		}
		
		return users;
	}, [props.data, ]);

	return <div style={{ marginTop: 15 }}>
		<div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
			<SegmentControl
				onClick={setReportType}
				selected={reportType}
				style={{ maxWidth: 600, flexGrow: 100 }}
				segments={[
					{ text: t('reports.income.types.totals'), value: ReportType.TOTALS },
					{ text: t('reports.income.types.promoter'), value: ReportType.PROMOTER },
					{ text: t('reports.income.types.assigned'), value: ReportType.ASSIGNED },
				]}
			/>
			<Button text={t('common.download')} color={'blue'} iconName={'file-download'} menu={(
				props.downloads.map(a=>({
					text: a.text,
					as: Link,
					to: a.url,
					target: '_blank',
					iconName: a.icon
				}))
			)} />
		</div>
		<Table style={{ maxWidth: 1200, margin: '15px auto' }}>
			<Table.Head data={[t(`reports.income.headers.report_${reportType}`, { returnObjects: true })]} />
			<Table.Body>
				{reportType===ReportType.TOTALS ? (
					props.data.income.map(a=>(
						<Table.Row key={`EVINC${a.event_id}`} collapsingIndexes={[0, 3, 4, 5, 6, 7, 8, 9]} data={[
							<Link className='normal' to={`/events/${a.event_id}/balance`}>{a.event_id}</Link>,
							<Link className='normal' to={`/events/${a.event_id}/balance`}>{a.event_name}</Link>,
							<Link className='normal' to={`/promoter/${a.promoter_id}`}>{a.promoter_name}</Link>,
							a.user_assigned ? (
								<Link className='normal' to={`/users/${a.user_assigned}`}>{a.user_assigned_name}</Link>
							) : <Icon name='minus' color='gray' />,
							<Money value={a.ticket_income} />,
							<Money value={a.commission} />,
							<Money value={a.delivery_cost} />,
							<Money value={a.payment_cost} />,
							<Money value={a.total_return} />,
							<Money value={a.ticket_income+a.commission+a.delivery_cost+a.payment_cost} />,
						]} />
					))
				) : reportType===ReportType.PROMOTER ? (
					promoter_data.map(a=>(
						<Table.Row
							key={`PRMT-${a.promoter_id}`}
							collapsingIndexes={[2, 3, 4, 5, 6, 7]}
							data={[
								<Link className='normal' to={`/promoter/${a.promoter_id}`}>{a.promoter_name}</Link>,
								a.user_assigned ? (
									<Link className='normal' to={`/users/${a.user_assigned}`}>{a.user_assigned_name}</Link>
								) : <Icon name='minus' color='gray' />,
								<Money value={a.events.length} />,
								<Money value={a.ticket_income} />,
								<Money value={a.commission} />,
								<Money value={a.delivery_cost} />,
								<Money value={a.payment_cost} />,
								<Money value={a.total_return} />,
								<Money value={a.ticket_income+a.commission+a.delivery_cost+a.payment_cost} />,
							]}
						/>
					))
				) : reportType===ReportType.ASSIGNED ? (
					assigned_data.map((a, i)=>(
						<Table.Row
							key={`USRASG-${(a.user_id || `I${i}`)}`}
							collapsingIndexes={[2, 3, 4, 5, 6, 7]}
							data={[
								a.user_id ? (
									<Link className='normal' to={`/users/${a.user_id}`}>{a.user_name}</Link>
								) : <Icon name='minus' color='gray' />,
								addCommas(a.promoters, false),
								addCommas(a.events.length, false),
								<Money value={a.ticket_income} />,
								<Money value={a.commission} />,
								<Money value={a.delivery_cost} />,
								<Money value={a.payment_cost} />,
								<Money value={a.total_return} />,
								<Money value={a.ticket_income+a.commission+a.delivery_cost+a.payment_cost} />,
							]}
						/>
					))
				) : null}
			</Table.Body>
		</Table>
	</div>
}

export default IncomeReport;